import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  Brain,
  CheckCircle2,
  FileSpreadsheet,
  Hand,
  History,
  RotateCcw,
  Sparkles,
} from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { inputClass } from '../ui/Input';
import { cn } from '../../utils/cn';
import {
  IDENTIFIER_FIELDS,
  STOCK_FIELDS,
  type AnalyzeColumn,
  type AnalyzeResponse,
  type Mapping,
  type MappingSource,
  type StockField,
  type StockImportMode,
} from './types';

const compactSelect = inputClass + ' h-8 text-xs';

interface Props {
  mode: StockImportMode;
  fileName: string;
  analysis: AnalyzeResponse;
  mapping: Mapping;
  sources: Partial<Record<StockField, MappingSource>>;
  busy: boolean;
  onAssign: (field: StockField, column: number | null) => void;
  onSheet: (sheet: string) => void;
  onHeaderRow: (row: number) => void;
  onReset: () => void;
  onChangeFile: () => void;
  onMode: (mode: StockImportMode) => void;
  /** A purchase invoice is received, never counted: no movement-type choice. */
  lockMode?: boolean;
}

/** Whether the mapping is complete enough to preview, and what is missing if not. */
export function mappingProblems(mapping: Mapping): ('quantity' | 'identifier')[] {
  const out: ('quantity' | 'identifier')[] = [];
  if (IDENTIFIER_FIELDS.every((f) => mapping[f] == null)) out.push('identifier');
  if (mapping.quantity == null) out.push('quantity');
  return out;
}

/**
 * Step 2 — the operator checks which column feeds which field. Two synchronised views of the same
 * mapping: the field list (left: what we need, what we guessed and why, sample values) and the file
 * itself (right: every column with its assignment in the header, so a column we did NOT use is as
 * visible as one we did).
 */
export function ColumnMappingStep(props: Props) {
  const { t } = useTranslation();
  const { analysis, mapping } = props;
  const byColumn = new Map<number, StockField>();
  for (const f of STOCK_FIELDS) if (mapping[f] != null) byColumn.set(mapping[f]!, f);

  const dataPreview = analysis.head
    .slice(analysis.headerRow + 1)
    .filter((r) => r.some((c) => c !== ''))
    .slice(0, 8);
  const headerRowOptions = analysis.head
    .map((r, i) => ({ i, label: r.filter(Boolean).slice(0, 4).join(' · ') }))
    .filter((o) => o.label);

  const groups: { key: string; title: string; hint: string; fields: StockField[] }[] = [
    {
      key: 'id',
      title: t('import.stock.map.groupIdentify'),
      hint: t('import.stock.map.groupIdentifyHint'),
      fields: ['sku', 'ean', 'manufacturerRef', 'brand'],
    },
    {
      key: 'qty',
      title: t('import.stock.map.groupQuantity'),
      hint: t(`import.stock.map.groupQuantityHint_${props.mode}`),
      fields: ['quantity'],
    },
    {
      key: 'opt',
      title: t('import.stock.map.groupOptional'),
      hint: t('import.stock.map.groupOptionalHint'),
      fields: ['docRef', 'note', 'description', 'unitPrice'],
    },
  ];
  const identifierOk = IDENTIFIER_FIELDS.some((f) => mapping[f] != null);
  const ignored = analysis.columns.filter((c) => !byColumn.has(c.index) && c.filled > 0);

  return (
    <div className="space-y-4">
      {!props.lockMode && analysis.suggestedMode && analysis.suggestedMode !== props.mode && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-warning/50 bg-warning/10 px-3 py-2 text-sm">
          <span>
            {t(`import.stock.map.modeHint_${analysis.suggestedMode}`, {
              header: analysis.columns[props.mapping.quantity ?? -1]?.header ?? '',
            })}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={props.busy}
            onClick={() => props.onMode(analysis.suggestedMode!)}
          >
            {t(`import.stock.map.switchTo_${analysis.suggestedMode}`)}
          </Button>
        </div>
      )}
      {/* File bar */}
      <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-muted/30 px-3 py-2.5">
        <div className="mr-auto flex min-w-0 items-center gap-2">
          <FileSpreadsheet className="h-5 w-5 shrink-0 text-primary" />
          <div className="min-w-0">
            <div className="truncate text-sm font-medium" title={props.fileName}>
              {props.fileName}
            </div>
            <div className="text-xs text-muted-foreground">
              {t('import.stock.map.fileStats', {
                rows: analysis.totalRows,
                columns: analysis.columns.length,
              })}
            </div>
          </div>
        </div>
        <div className={cn('text-xs text-muted-foreground', props.lockMode && 'hidden')}>
          <span className="mb-1 block font-medium">{t('import.stock.movementType')}</span>
          <div role="radiogroup" className="inline-flex h-8 rounded-md border bg-background p-0.5">
            {(['RECEIPT', 'INVENTORY'] as const).map((m) => (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={props.mode === m}
                disabled={props.busy}
                onClick={() => props.onMode(m)}
                className={cn(
                  'rounded px-2.5 text-xs font-medium transition-colors',
                  props.mode === m
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent'
                )}
              >
                {t(`import.stock.modeShort_${m}`)}
              </button>
            ))}
          </div>
        </div>
        {analysis.sheets.length > 1 && (
          <label className="text-xs text-muted-foreground">
            <span className="mb-1 block font-medium">{t('import.stock.map.sheet')}</span>
            <select
              className={compactSelect + ' w-44'}
              value={analysis.sheet ?? ''}
              disabled={props.busy}
              onChange={(e) => props.onSheet(e.target.value)}
            >
              {analysis.sheets.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="text-xs text-muted-foreground">
          <span className="mb-1 block font-medium">{t('import.stock.map.headerRow')}</span>
          <select
            className={compactSelect + ' w-64'}
            value={analysis.headerRow}
            disabled={props.busy}
            onChange={(e) => props.onHeaderRow(Number(e.target.value))}
          >
            <option value={-1}>{t('import.stock.map.noHeaderRow')}</option>
            {headerRowOptions.map((o) => (
              <option key={o.i} value={o.i}>
                {t('import.stock.map.rowN', { n: o.i + 1 })} — {o.label.slice(0, 48)}
              </option>
            ))}
          </select>
        </label>
        <Button variant="outline" size="sm" onClick={props.onChangeFile} disabled={props.busy}>
          {t('import.stock.map.changeFile')}
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]">
        {/* Field list */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Sparkles className="h-3.5 w-3.5 text-primary" /> {t('import.stock.map.intro')}
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={props.onReset}
              disabled={props.busy}
              title={t('import.stock.map.resetHint')}
            >
              <RotateCcw className="h-3.5 w-3.5" /> {t('import.stock.map.reset')}
            </Button>
          </div>
          {groups.map((g) => (
            <section key={g.key} className="rounded-lg border">
              <header className="flex items-start justify-between gap-2 border-b bg-muted/40 px-3 py-2">
                <div>
                  <h3 className="text-sm font-semibold">{g.title}</h3>
                  <p className="text-xs text-muted-foreground">{g.hint}</p>
                </div>
                {g.key === 'id' &&
                  (identifierOk ? (
                    <CheckCircle2
                      className="mt-0.5 h-4 w-4 shrink-0 text-success"
                      aria-label={t('import.stock.map.ok')}
                    />
                  ) : (
                    <AlertTriangle
                      className="mt-0.5 h-4 w-4 shrink-0 text-destructive"
                      aria-label={t('import.stock.map.missing')}
                    />
                  ))}
              </header>
              <div className="divide-y">
                {g.fields.map((f) => (
                  <FieldRow
                    key={f}
                    field={f}
                    mode={props.mode}
                    columns={analysis.columns}
                    column={mapping[f]}
                    source={props.sources[f]}
                    suggestion={analysis.suggestions[f]}
                    byColumn={byColumn}
                    disabled={props.busy}
                    onAssign={(c) => props.onAssign(f, c)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* File preview with per-column assignment */}
        <div className="min-w-0 space-y-2">
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="text-sm font-semibold">{t('import.stock.map.previewTitle')}</h3>
            <span className="text-xs text-muted-foreground">
              {t('import.stock.map.previewHint', {
                shown: dataPreview.length,
                total: analysis.totalRows,
              })}
            </span>
          </div>
          <div className={cn('overflow-auto rounded-lg border', props.busy && 'opacity-60')}>
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr>
                  {analysis.columns.map((c) => {
                    const f = byColumn.get(c.index);
                    return (
                      <th
                        key={c.index}
                        className={cn(
                          'min-w-[9rem] border-b border-r px-2 py-2 text-left align-top font-normal last:border-r-0',
                          f ? 'bg-primary/10' : 'bg-muted/40'
                        )}
                      >
                        <div className="mb-1 flex items-center gap-1.5">
                          <span className="rounded bg-background px-1 font-mono text-[10px] text-muted-foreground">
                            {c.letter}
                          </span>
                          <span
                            className={cn(
                              'truncate font-medium',
                              !c.header && 'italic text-muted-foreground'
                            )}
                            title={c.header}
                          >
                            {c.header || t('import.stock.map.noHeader')}
                          </span>
                        </div>
                        <select
                          aria-label={t('import.stock.map.columnAssign', { column: c.letter })}
                          className={cn(
                            compactSelect,
                            'h-7 px-1.5',
                            f
                              ? 'border-primary/50 font-medium text-primary'
                              : 'text-muted-foreground'
                          )}
                          value={f ?? ''}
                          disabled={props.busy}
                          onChange={(e) => {
                            const next = e.target.value as StockField | '';
                            if (next) props.onAssign(next, c.index);
                            else if (f) props.onAssign(f, null);
                          }}
                        >
                          <option value="">{t('import.stock.map.ignore')}</option>
                          {STOCK_FIELDS.map((sf) => (
                            <option key={sf} value={sf}>
                              → {t(`import.stock.fields.${sf}`)}
                            </option>
                          ))}
                        </select>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {dataPreview.map((r, ri) => (
                  <tr key={ri} className="border-b last:border-b-0">
                    {analysis.columns.map((c) => (
                      <td
                        key={c.index}
                        className={cn(
                          'max-w-[14rem] truncate border-r px-2 py-1.5 font-mono last:border-r-0',
                          byColumn.has(c.index)
                            ? 'bg-primary/5 text-foreground'
                            : 'text-muted-foreground/70'
                        )}
                        title={r[c.index]}
                      >
                        {r[c.index] || <span className="text-muted-foreground/40">—</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {ignored.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {t('import.stock.map.ignoredColumns', {
                columns: ignored
                  .map((c) => `${c.letter}${c.header ? ` (${c.header})` : ''}`)
                  .join(', '),
              })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function FieldRow(props: {
  field: StockField;
  mode: StockImportMode;
  columns: AnalyzeColumn[];
  column: number | null;
  source?: MappingSource;
  suggestion?: AnalyzeResponse['suggestions'][StockField];
  byColumn: Map<number, StockField>;
  disabled: boolean;
  onAssign: (column: number | null) => void;
}) {
  const { t } = useTranslation();
  const { field, column } = props;
  const col = column != null ? props.columns[column] : undefined;
  const required = field === 'quantity';
  const label =
    field === 'quantity'
      ? t(`import.stock.fields.quantity_${props.mode}`)
      : t(`import.stock.fields.${field}`);

  return (
    <div className={cn('px-3 py-2.5', column == null && required && 'bg-destructive/5')}>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <span className="text-sm font-medium">{label}</span>
          {required && (
            <span className="ml-1 text-destructive" aria-hidden>
              *
            </span>
          )}
          <p className="text-xs text-muted-foreground">{t(`import.stock.fieldHints.${field}`)}</p>
        </div>
        <SourceChip
          source={props.source}
          column={column}
          suggestion={props.suggestion}
          required={required}
          field={field}
        />
      </div>
      <select
        className={cn(compactSelect, column == null && 'text-muted-foreground')}
        value={column ?? ''}
        disabled={props.disabled}
        onChange={(e) => props.onAssign(e.target.value === '' ? null : Number(e.target.value))}
      >
        <option value="">{t('import.stock.map.notInFile')}</option>
        {props.columns.map((c) => {
          const usedBy = props.byColumn.get(c.index);
          return (
            <option key={c.index} value={c.index}>
              {c.letter} · {c.header || t('import.stock.map.noHeader')}
              {usedBy && usedBy !== field
                ? `  (${t('import.stock.map.usedFor', { field: t(`import.stock.fields.${usedBy}`) })})`
                : ''}
              {c.filled === 0 ? `  (${t('import.stock.map.empty')})` : ''}
            </option>
          );
        })}
      </select>
      {col && col.samples.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {col.samples.slice(0, 4).map((s, i) => (
            <span
              key={i}
              className="max-w-[10rem] truncate rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground"
              title={s}
            >
              {s}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function SourceChip({
  source,
  column,
  suggestion,
  required,
  field,
}: {
  source?: MappingSource;
  column: number | null;
  suggestion?: AnalyzeResponse['suggestions'][StockField];
  required: boolean;
  field: StockField;
}) {
  const { t } = useTranslation();
  if (column == null) {
    return required ? <Badge variant="destructive">{t('import.stock.map.required')}</Badge> : null;
  }
  const chip = (
    variant: 'success' | 'warning' | 'muted' | 'outline',
    icon: JSX.Element,
    text: string,
    title?: string
  ) => (
    <Badge variant={variant} className="shrink-0 gap-1 whitespace-nowrap" title={title}>
      {icon}
      {text}
    </Badge>
  );
  switch (source) {
    case 'REMEMBERED':
      return chip(
        'outline',
        <History className="h-3 w-3" />,
        t('import.stock.map.srcRemembered'),
        t('import.stock.map.srcRememberedHint')
      );
    case 'MANUAL':
      return chip('muted', <Hand className="h-3 w-3" />, t('import.stock.map.srcManual'));
    case 'VALUES':
      return chip(
        'success',
        <Brain className="h-3 w-3" />,
        t('import.stock.map.srcValues', { pct: Math.round((suggestion?.matchRate ?? 0) * 100) }),
        t(`import.stock.map.srcValuesHint_${field}`, { defaultValue: '' }) || undefined
      );
    case 'HEADER':
      return (suggestion?.confidence ?? 0) >= 0.85
        ? chip('success', <Sparkles className="h-3 w-3" />, t('import.stock.map.srcHeader'))
        : chip(
            'warning',
            <AlertTriangle className="h-3 w-3" />,
            t('import.stock.map.srcHeaderWeak')
          );
    case 'PATTERN':
      return chip(
        'warning',
        <AlertTriangle className="h-3 w-3" />,
        t('import.stock.map.srcPattern'),
        t('import.stock.map.srcPatternHint')
      );
    default:
      return null;
  }
}
